<template>
  <div class="bg-gray-100 rounded font-condensed position-relative overflow-hidden d-lg-none">
    <overlay-wrapper :show="loading">
      <div
        v-show="isSomethingBefore"
        class="inpage-nav-arrows inpage-before box-shadow rounded-start"
        @click="scrollBack"
      >
        <div class="icon-wrapper d-flex align-items-center">
          <span class="icon" />
        </div>
      </div>

      <BNav
        ref="navigationList"
        class="inpage-nav inpage-nav-horizontal d-md-flex flex-nowrap text-center"
        data-test-id="inpage-nav-mobile"
      >
        <li
          v-for="(item, i) in items"
          :key="'page_navigation_item_' + i"
          class="nav-item"
          :class="{'disabled':item.disabled}"
        >
          <router-link
            v-if="!item.link"
            class="nav-link d-inline-flex flex-column"
            :to="{ path: item.path, query: { serial: getCurrentSerial, [constants.REGION_QUERY_NAME]: getRegion } }"
            :data-test-id="'nav-page-vertical-item_' + i"
            @click="onNavLinkClick($event, item.disabled)"
          >
            <b-icon
              v-if="item.icon && item.icon !== 'null'"
              :icon="item.icon"
              class="text-large half-height d-flex align-items-end"
              fixed-width
            />
            <div class="half-height d-flex align-items-center">{{ translate(item.title.ck, labels, item.title.cp) }}</div>
          </router-link>
          <a
            v-if="item.link"
            :href="getHrefLink(item)"
            target="_blank"
            class="nav-link d-inline-flex flex-column px-1"
            :data-test-id="'nav-page-vertical-item_' + i"
          >
            <b-icon
              v-if="item.icon && item.icon !== 'null'"
              :icon="item.icon"
              class="text-large half-height d-flex align-items-end"
              fixed-width
            />
            <div class="half-height d-flex align-items-center px-3">
              <span class="position-relative">
                {{ translate(item.title.ck, labels, item.title.cp) }}
                <b-icon
                  class="text-small ms-1 position-absolute"
                  icon="bobcaticon-create"
                />
              </span>
            </div>
          </a>
        </li>
      </BNav>

      <div
        v-show="isSomethingAfter"
        class="inpage-nav-arrows inpage-next box-shadow rounded-end"
        @click="scrollForth"
      >
        <div class="icon-wrapper d-flex align-items-center">
          <span class="icon" />
        </div>
      </div>

      <div
        v-if="disabled"
        class="mask disabled"
      />
    </overlay-wrapper>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue'
import {useStore} from 'vuex'
import constants from '@/constants'
import {useCommon} from '@/composables/useCommon'
import debounce from 'lodash.debounce'

const props = withDefaults(defineProps<{
  disabled?: boolean
  loading?: boolean
  items: any[]
}>(), {
  disabled: false,
  loading: true
})

const store = useStore()
const {labels, translate, translatedLabel} = useCommon()

const navigationList = ref<any>(null)
const scrollPosition = ref(0)
const screenWidth = ref(0)
const navigationWidth = ref(0)

const getRegion = computed(() => store.getters['mymachine/getRegion'])
const getCurrentSerial = computed(() => store.getters['mymachine/getCurrentSerial'])

const isSomethingBefore = computed(() => scrollPosition.value > 0)
const isSomethingAfter = computed(() => {
  if (scrollPosition.value === 0 && navigationWidth.value === screenWidth.value) return 0
  return Math.floor(navigationWidth.value - screenWidth.value - scrollPosition.value) > 1
})

const handleResize = debounce(handleScroll, 100)

watch(() => props.loading, (loading) => {
  if (!loading && getNavigationElement()) {
    nextTick(() => scrollToActive())
  }
})

onMounted(() => {
  if (getNavigationElement()) {
    handleScroll()
    getNavigationElement()?.addEventListener('scroll', handleScroll)
  }
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  getNavigationElement()?.removeEventListener('scroll', handleScroll)
  window.removeEventListener('resize', handleResize)
})

function getNavigationElement(): HTMLElement | null {
  if (!navigationList.value) return null
  return navigationList.value.$el ? navigationList.value.$el as HTMLElement : navigationList.value as HTMLElement
}

function getHrefLink(item: any): string {
  const link = translatedLabel(item.link.ck, item.link.cp)
  if (link.includes('http')) return link
  return '#'
}

function onNavLinkClick(event: Event, disabled?: boolean): void {
  if (!disabled) return
  event.preventDefault()
  event.stopPropagation()
}

function handleScroll(): void {
  const nav = getNavigationElement()
  navigationWidth.value = nav?.scrollWidth || 0
  screenWidth.value = nav?.offsetWidth || 0
  scrollPosition.value = nav?.scrollLeft || 0
}

function scrollBack(): void {
  const nav = getNavigationElement()
  if (nav) nav.scrollLeft -= screenWidth.value / 4
}

function scrollForth(): void {
  const nav = getNavigationElement()
  if (nav) nav.scrollLeft += screenWidth.value / 4
}

function scrollToActive(): void {
  const nav = getNavigationElement()
  const listItem = nav?.querySelector('li') as HTMLElement | null
  if (!nav || !listItem) return

  const itemWidth = listItem.offsetWidth
  const maxItemsVisible = Math.floor(screenWidth.value / itemWidth)
  const currentActive = props.items.map((x: any) => x.active).indexOf(true) + 1
  const scrollOffset = currentActive - maxItemsVisible

  nextTick(() => {
    nav.scrollLeft += scrollOffset * itemWidth
  })
}
</script>

<style lang="scss">
@import '../../scss/colors';
@import '../../scss/transitions';

.inpage-nav {
  &-horizontal {
    overflow-x: auto;
    scroll-behavior: smooth;

    .nav-item {
      min-width: 6.5rem;
      min-height: 5rem;
      width: 25%;

      .nav-link {
        font-size: 0.875rem;
        height: 100%;
        width: 100%;
        padding: 0;
        line-height: 1rem;
        display: flex;
        transition: $transition-base;

        .half-height {
          flex: 0 0 50%;
          justify-content: center;
        }
      }
    }
  }

  &-arrows {
    cursor: pointer;
    background: $white;
    border-radius: .5rem;
    height: 100%;
    position: absolute;
    text-align: center;
    top: 0;
    width: 1.125rem;

    .icon-wrapper {
      height: 100%;
      padding: .25rem;
    }

    .icon {
      border: solid $orange;
      border-width: 0 3px 3px 0;
      display: inline-block;
      padding: 3px;
    }

    &.inpage {
      &-next,
      &-before {
        z-index: 2;
      }

      &-before {
        left: 0;

        .icon {
          transform: rotate(135deg);
        }
      }

      &-next {
        right: 0;

        .icon {
          transform: rotate(-45deg);
        }
      }
    }
  }
}
</style>
